export interface AdminActor {
  readonly id: string;
  readonly roles: readonly string[];
  readonly permissions: readonly string[];
  readonly mfaSatisfied: boolean;
}

export interface AdminBootstrap {
  readonly actor: AdminActor;
  readonly flags: Readonly<Record<string, boolean>>;
}

export type MfaEnrollmentStatus = 'NOT_ENROLLED' | 'PENDING' | 'ACTIVE';

export interface MfaStatus {
  readonly status: MfaEnrollmentStatus;
  readonly mfaSatisfied: boolean;
}

export interface MfaStepUp {
  readonly mfaSatisfied: true;
  readonly mfaSatisfiedUntil: string;
}

export type AdminRecord = Readonly<Record<string, unknown>>;

export interface MembershipPlanCreateInput {
  readonly code: string;
  readonly name: string;
  readonly priceMinor: number;
  readonly currency: string;
}

export interface MembershipBenefitRuleCreateInput {
  readonly planVersionId: string;
  readonly minimumOrderMinor: number;
  readonly maximumDistanceMeters: number | null;
  readonly deliveryType: 'STANDARD' | 'EXPRESS' | 'OVERNIGHT';
  readonly startMinute: number | null;
  readonly endMinute: number | null;
  readonly usageLimitPerPeriod: number | null;
  readonly waiveBaseFee: boolean;
  readonly feeReductionMinor: number;
  readonly marketIds: readonly string[];
  readonly sellerIds: readonly string[];
}

export interface AdminMembership extends AdminRecord {
  readonly id: string;
  readonly user_id: string;
  readonly plan_id: string;
  readonly plan_version_id: string;
  readonly status: string;
  readonly current_period_start: string | null;
  readonly current_period_end: string | null;
  readonly cancel_at_period_end: boolean;
  readonly renewal_enabled: boolean;
}

export interface AdminMembershipBillingAttempt extends AdminRecord {
  readonly id: string;
  readonly membership_id: string;
  readonly kind: string;
  readonly amount_minor: number;
  readonly currency: string;
  readonly provider: string;
  readonly provider_reference: string | null;
  readonly outcome: string;
  readonly failure_reason: string | null;
  readonly attempt_number: number;
  readonly attempted_at: string;
}

export type MarketStatus = 'PLANNED' | 'WAITLIST' | 'LIVE' | 'PAUSED' | 'RETIRED';

export interface AdminLead extends AdminRecord {
  readonly id: string;
  readonly status: string;
}

export interface AcquisitionSource {
  readonly source: string;
  readonly medium: string;
  readonly campaign: string;
  readonly visitors: number;
  readonly attributedOrders: number;
}

export interface SearchTermInsight {
  readonly term: string;
  readonly marketId: string | null;
  readonly searchCount: number;
  readonly zeroResultCount: number;
  readonly lastSearchedAt: string;
}

export type AdminDeliveryLifecycleStatus =
  'REQUESTED' | 'DISPATCHED' | 'PICKED_UP' | 'IN_TRANSIT' | 'DELIVERED' | 'FAILED' | 'UNAVAILABLE';

export interface AdminDeliveryAssignment {
  readonly assignmentId: string;
  readonly lifecycleStatus: AdminDeliveryLifecycleStatus;
  readonly assignmentStatus: string;
  readonly fulfilment: {
    readonly id: string;
    readonly orderId: string;
    readonly subOrderId: string;
    readonly status: string;
    readonly mode: string;
    readonly retailerId: string;
    readonly locationId: string;
    readonly marketId: string | null;
  };
  readonly provider: {
    readonly adapterKind: string;
    readonly integrationId: string | null;
    readonly displayName: string | null;
    readonly assignmentReference: string | null;
    readonly environment: 'MOCK' | 'SANDBOX' | 'LIVE';
    readonly isMock: boolean;
  };
  readonly requestedAt: string;
  readonly dispatchedAt: string | null;
  readonly courierAcceptedAt: string | null;
  readonly trackingEvents: readonly AdminRecord[];
  readonly courierEvents: readonly AdminRecord[];
}

export interface AdminDirectListing extends AdminRecord {
  readonly id: string;
}

export type AdminStoreStatus = 'OPEN' | 'PAUSED' | 'SUSPENDED' | 'CLOSED';

export interface AdminStoreLocation {
  readonly retailer: { readonly id: string; readonly name: string };
  readonly location: {
    readonly id: string;
    readonly name: string;
    readonly status: AdminStoreStatus;
  };
  readonly market: { readonly id: string; readonly name: string } | null;
  /** This state is composed by the API; screens must not derive availability. */
  readonly availability: {
    readonly label: string;
    readonly labelText: string;
    readonly openNow: boolean;
    readonly openLate: boolean;
    readonly overnightAvailable: boolean;
    readonly twentyFourHours: boolean;
    readonly withheldReasons: readonly string[];
  };
}

export interface AdminImportJob {
  readonly id: string;
  readonly retailer: { readonly id: string; readonly name: string };
  readonly location: { readonly id: string; readonly name: string };
  readonly file: {
    readonly originalName: string | null;
    readonly format: string;
    readonly scanStatus: string | null;
    readonly scanProvider: string | null;
    readonly scannedAt: string | null;
  };
  readonly processing: {
    readonly quarantineStatus: 'HELD' | 'CLEAR';
    readonly holdReason: string | null;
    readonly quarantinedAt: string | null;
    readonly releasedAt: string | null;
    readonly purgedAt: string | null;
  };
  readonly job: {
    readonly step: string;
    readonly status: string;
    readonly totalRows: number;
    readonly warningCount: number;
    readonly errorCount: number;
    readonly createdAt: string;
  };
}

export interface AdminServiceArea {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly status: MarketStatus;
  readonly deliveryEnabled: boolean;
  readonly pickupEnabled: boolean;
  readonly overnightEnabled: boolean;
  readonly priority: number;
}

export interface AdminMarket {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly region: string | null;
  readonly country_code: string;
  readonly timezone: string;
  readonly status: MarketStatus;
  readonly waitlist_message: string | null;
  readonly sort_order: number;
  readonly service_areas: readonly AdminServiceArea[];
}

export type AdminAuthOutcome =
  | { readonly ok: true }
  | { readonly ok: false; readonly kind: 'invalid_credentials'; readonly message: string }
  | { readonly ok: false; readonly kind: 'mfa_required'; readonly message: string };

/** Authentication must not reveal whether an administrator account exists. */
export const GENERIC_ADMIN_AUTH_FAILURE = 'Email or password is incorrect.';

export interface AdminMembershipPlanVersion {
  readonly id: string;
  readonly version: number;
  readonly name_snapshot: string;
  readonly price_minor: number;
  readonly currency: string;
  readonly interval_unit: string;
  readonly retired_at: string | null;
}

export interface AdminMembershipPlan {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly status: string;
  readonly versions: readonly AdminMembershipPlanVersion[];
}

export interface AdminApiClient {
  readonly auth: {
    signIn(input: { readonly email: string; readonly password: string }): Promise<AdminAuthOutcome>;
  };
  readonly bootstrap: { get(): Promise<AdminBootstrap> };
  /** MFA material is deliberately transient: callers must never persist these responses. */
  readonly mfa: {
    status(): Promise<MfaStatus>;
    beginTotp(): Promise<{ readonly status: 'PENDING'; readonly provisioningUri: string }>;
    confirmTotp(
      code: string,
    ): Promise<{ readonly status: 'ACTIVE'; readonly recoveryCodes: readonly string[] }>;
    stepUp(code: string): Promise<MfaStepUp>;
  };
  readonly overview: { get(): Promise<AdminRecord> };
  readonly retailers: {
    applications(): Promise<readonly AdminRecord[]>;
    approve(retailerId: string): Promise<AdminRecord>;
    reject(retailerId: string, reason: string): Promise<AdminRecord>;
    requestInfo(
      applicationId: string,
      reason: string,
      fields: readonly string[],
    ): Promise<AdminRecord>;
  };
  readonly users: {
    list(input?: { readonly limit?: number; readonly offset?: number }): Promise<{
      readonly users: readonly AdminRecord[];
      readonly limit?: number;
      readonly offset?: number;
    }>;
    detail(
      userId: string,
    ): Promise<{ readonly user: AdminRecord; readonly roles: readonly AdminRecord[] }>;
    grantRole(
      userId: string,
      input: { roleKey: string; retailerId?: string; reason: string },
    ): Promise<AdminRecord>;
    revokeRole(userId: string, roleKey: string, reason: string): Promise<AdminRecord>;
    suspend(
      userId: string,
      reason: string,
    ): Promise<{ readonly userId: string; readonly status: string }>;
    reactivate(
      userId: string,
      reason: string,
    ): Promise<{ readonly userId: string; readonly status: string }>;
  };
  readonly invitations: {
    list(input?: {
      readonly status?: 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';
      readonly limit?: number;
      readonly offset?: number;
    }): Promise<{
      readonly invitations: readonly AdminRecord[];
      readonly limit?: number;
      readonly offset?: number;
    }>;
    create(input: {
      readonly email: string;
      readonly roleKey: string;
      readonly retailerId?: string;
      readonly expiresInDays?: number;
      readonly reason: string;
    }): Promise<AdminRecord>;
    revoke(invitationId: string, reason: string): Promise<AdminRecord>;
  };
  readonly flags: {
    list(): Promise<readonly AdminRecord[]>;
    update(
      key: string,
      input: {
        enabled: boolean;
        scopeKind: 'global' | 'market' | 'retailer' | 'location' | 'member_group';
        scopeId?: string | null;
        rolloutPercentage?: number | null;
        reason: string;
        founderApproved?: boolean;
      },
    ): Promise<AdminRecord>;
  };
  readonly audit: { list(): Promise<{ readonly entries: readonly AdminRecord[] }> };
  readonly leads?: {
    list(): Promise<{
      readonly customer: readonly AdminLead[];
      readonly retailer: readonly AdminLead[];
    }>;
    updateStatus(
      leadId: string,
      input: {
        readonly kind: 'CUSTOMER' | 'RETAILER' | 'MEMBERSHIP';
        readonly status: 'NEW' | 'CONTACTED' | 'QUALIFIED' | 'CONVERTED' | 'NURTURE' | 'CLOSED';
        readonly notes?: string;
      },
    ): Promise<void>;
  };
  readonly insights?: {
    acquisition(): Promise<{ readonly sources: readonly AcquisitionSource[] }>;
    searchTerms(): Promise<{
      readonly topTerms: readonly SearchTermInsight[];
      readonly zeroResultTerms: readonly SearchTermInsight[];
    }>;
  };
  readonly orders: {
    list(input?: { readonly limit?: number; readonly offset?: number }): Promise<{
      readonly orders: readonly AdminRecord[];
      readonly limit?: number;
      readonly offset?: number;
    }>;
    detail(orderId: string): Promise<{
      readonly order: AdminRecord;
      readonly subOrders: readonly AdminRecord[];
      readonly lines: readonly AdminRecord[];
    }>;
    createException(
      orderId: string,
      input: {
        readonly reason:
          | 'DAMAGED'
          | 'INCORRECT_ITEM'
          | 'MISSING_ITEM'
          | 'NOT_DELIVERED'
          | 'UNDELIVERABLE'
          | 'CANCELLED'
          | 'FAILED'
          | 'DUPLICATE'
          | 'DEFECTIVE';
        readonly note?: string;
      },
    ): Promise<AdminRecord>;
  };
  /** Preserved for the future `returns.refunds` release; do not surface at launch. */
  readonly refunds: {
    list(): Promise<{ readonly returns: readonly AdminRecord[] }>;
    decide(
      returnRequestId: string,
      status: 'approved' | 'rejected',
      reason: string,
    ): Promise<AdminRecord>;
  };
  /** Launch-only administrator-operated operational exceptions. */
  readonly exceptions?: {
    list(): Promise<{ readonly exceptions: readonly AdminRecord[] }>;
    resolve(
      exceptionId: string,
      input: {
        readonly outcome: 'REDELIVER' | 'CANCEL' | 'RETURN_FUNDS';
        readonly orderLineId?: string;
        readonly amountMinor?: number;
        readonly note?: string;
      },
    ): Promise<AdminRecord>;
  };
  readonly delivery: {
    jobs(input: {
      readonly status?: AdminDeliveryLifecycleStatus;
      readonly market?: string;
      readonly limit?: number;
      readonly offset?: number;
    }): Promise<{
      readonly assignments: readonly Omit<
        AdminDeliveryAssignment,
        'trackingEvents' | 'courierEvents'
      >[];
      readonly limit: number;
      readonly offset: number;
    }>;
    detail(assignmentId: string): Promise<AdminDeliveryAssignment>;
  };
  readonly directInventory: {
    listings(locationId?: string): Promise<readonly AdminDirectListing[]>;
  };
  readonly stores: {
    list(input?: {
      readonly market?: string;
      readonly status?: AdminStoreStatus;
    }): Promise<readonly AdminStoreLocation[]>;
  };
  readonly imports: {
    jobs(input?: {
      readonly retailerId?: string;
      readonly status?: string;
      readonly quarantine?: 'HELD' | 'CLEAR';
      readonly limit?: number;
      readonly offset?: number;
    }): Promise<{
      readonly jobs: readonly AdminImportJob[];
      readonly limit: number;
      readonly offset: number;
    }>;
    releaseQuarantine(importJobId: string): Promise<AdminImportJob>;
  };
  readonly payouts: {
    list(): Promise<readonly AdminRecord[]>;
    finalise(payoutRunId: string): Promise<AdminRecord>;
  };
  readonly commissions: {
    get(): Promise<AdminRecord>;
    update(input: AdminRecord): Promise<AdminRecord>;
  };
  readonly membership: {
    listPlans(): Promise<readonly AdminMembershipPlan[]>;
    createPlan(input: MembershipPlanCreateInput): Promise<AdminRecord>;
    createBenefitRule(input: MembershipBenefitRuleCreateInput): Promise<AdminRecord>;
    listMemberships(): Promise<readonly AdminMembership[]>;
    billingAttempts(membershipId: string): Promise<readonly AdminMembershipBillingAttempt[]>;
  };
  readonly markets: {
    list(): Promise<readonly AdminMarket[]>;
    update(
      marketId: string,
      input: { readonly status: MarketStatus; readonly reason: string },
    ): Promise<AdminMarket>;
    createServiceArea(
      marketId: string,
      input: {
        readonly slug: string;
        readonly name: string;
        readonly status: MarketStatus;
        readonly deliveryEnabled: boolean;
        readonly pickupEnabled: boolean;
        readonly overnightEnabled: boolean;
        readonly priority: number;
        readonly reason: string;
      },
    ): Promise<AdminServiceArea>;
    updateServiceArea(
      serviceAreaId: string,
      input: {
        readonly status: MarketStatus;
        readonly deliveryEnabled: boolean;
        readonly pickupEnabled: boolean;
        readonly overnightEnabled: boolean;
        readonly priority: number;
        readonly reason: string;
      },
    ): Promise<AdminServiceArea>;
  };
}
